import moment from 'moment';
import ReactCountryFlag from 'react-country-flag';
import { FaUserAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { IoCall } from 'react-icons/io5';
import { MdEmail } from 'react-icons/md';
import { Client } from '../../../types/users';
import { showFullName, showPicture } from '../../../utils/commonFunctions';
import AssociateRequestInfo from '../../AssociateRequestsComps/AssociateRequestInfo';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const ClientDetailsModal = ({
  client,
  handleModalClose,
}: {
  client: Client;
  handleModalClose: () => void;
}) => {
  // destructuring the client object here
  const {
    username,
    email,
    first_name,
    middle_name,
    last_name,
    last_login_date,
    city_name,
    province_name,
    iso_code,
    country_name,
    postal_code,
    address,
    isd_code,
    phone,
    profile_picture,
    is_active,
  } = client || {};

  // rendering client details modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[750px]'>
      <ModalTitle titleText='client details' />

      <div className='border-t-2 border-dashed border-base-dark-blue pt-2'>
        <div className='flex flex-col md:flex-row items-center md:items-start justify-between'>
          <div>
            <h3 className='text-2xl font-medium text-base-dark-blue'>
              {showFullName(first_name, middle_name, last_name, true)}
            </h3>

            <p className='text-base-cyan text-center md:text-left font-semibold italic'>
              @{username}
            </p>
          </div>

          <div>
            <p
              className={`text-lg text-center md:text-right ${
                is_active ? 'text-base-cyan' : 'text-base-red'
              }`}
            >
              Status:{' '}
              <span className='font-semibold'>
                {is_active ? 'Active' : 'Deactive'}
              </span>
            </p>

            <span className='block text-xs text-center md:text-right text-base-light-blue'>
              Last Login:{' '}
              <span className='font-semibold'>
                {last_login_date
                  ? moment(last_login_date).format('DD-MM-YYYY @hh:mm A')
                  : 'N/A'}
              </span>
            </span>
          </div>
        </div>

        <div className='flex flex-col-reverse md:flex-row items-center justify-between mt-3 gap-3'>
          <div className='flex flex-col justify-center flex-1 min-h-[150px] gap-1 bg-gray-100 rounded-lg px-4 py-2'>
            {country_name ? (
              <AssociateRequestInfo
                icon={
                  <ReactCountryFlag
                    style={{ width: '1.5em', height: '1.42em' }}
                    countryCode={iso_code}
                    svg
                  />
                }
                details={
                  <span className='font-semibold text-legal-dark-green'>
                    {country_name}
                  </span>
                }
              />
            ) : null}

            <AssociateRequestInfo
              icon={<FaLocationDot className='size-5' />}
              details={
                address ? (
                  <>
                    From{' '}
                    <span className='font-semibold text-legal-dark-green'>
                      {address}, {city_name}, {province_name}, {postal_code}
                    </span>
                  </>
                ) : (
                  'N/A'
                )
              }
            />

            <AssociateRequestInfo
              icon={<MdEmail className='size-5' />}
              details={
                <span className='font-semibold text-base-orange'>{email}</span>
              }
            />

            <AssociateRequestInfo
              icon={<IoCall className='size-5' />}
              details={
                phone ? (
                  <span className='font-semibold text-legal-dark-green'>
                    {`${isd_code}${phone}`}
                  </span>
                ) : (
                  'N/A'
                )
              }
            />
          </div>

          <div>
            {profile_picture ? (
              <div className='size-[150px] rounded-lg overflow-hidden'>
                <img
                  className='w-full h-full object-cover'
                  src={showPicture(profile_picture)}
                  alt={`avatar of ${username}`}
                />
              </div>
            ) : (
              <div className='flex items-center justify-center bg-gray-100 size-[150px] rounded-lg'>
                <FaUserAlt className='size-20 text-gray-300' />
              </div>
            )}
          </div>
        </div>
      </div>
    </ParentModal>
  );
};

export default ClientDetailsModal;
